import React, { useEffect, useState, useCallback, useMemo, useImperativeHandle, forwardRef } from 'react';
import { Form, Input, Select, Button, Spin } from 'antd';
import debounce from 'lodash/debounce';
import styles from './Digests-Template.module.scss';
import { useTypedSelector } from 'hooks/useTypedSelector';
import { useActions } from 'hooks/useActions';
import { ReactComponent as ThreeDotsSvg } from 'assets/svg/threedots.svg';
import { useDebounce } from 'hooks/debounceSearch';

import * as AntdIcons from '@ant-design/icons';

type AntdIconType = keyof typeof AntdIcons;

const getIconList = (): AntdIconType[] => {
  return Object.keys(AntdIcons)
    .filter(key => 
      key.endsWith('Outlined') || 
      key.endsWith('Filled') || 
      key.endsWith('TwoTone')
    ) as AntdIconType[];
};


export type CreateTemplate = {
    digestId: string;
    description: string;
    category: string;
    name: string;
}

export type FormRef = {
    submit: () => void;
    clear: () => void;
};


const { Option } = Select;

const renderIcon = (iconName: AntdIconType) => {
  const IconComponent = AntdIcons[iconName] as React.ComponentType;
  return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <IconComponent />
          <span>{iconName}</span>
      </div>
  );
};

const DigestTemplateForm = forwardRef<FormRef, {
    onClose: () => void,
    setFormData: React.Dispatch<React.SetStateAction<CreateTemplate | undefined>>
  }>(({ onClose, setFormData }, ref) =>  {
    const [form] = Form.useForm();
    useImperativeHandle(ref, () => ({
        submit: async () => {
            const values = await form.validateFields();
            return values;
        },
        clear: async () => {
            return form.resetFields()
        }
      }));
    const [searchText, setSearchText] = useState('');
    const [loading, setLoading] = useState(false);
    const { searchDigestsData, getTemplateCategoryDefaults } = useActions();
    const { searchResult: digests  } = useTypedSelector((state) => state.digestSearchResult);
    const { digestTemplateCategories } = useTypedSelector((state) => state.digestsTemplatesCategories);
  
    const debouncedSearch = useDebounce<string>(searchText, 800);

    useEffect(() => {
        getTemplateCategoryDefaults();
    }, []);

    useEffect(() => {
        searchDigestsData({
            searchParams: debouncedSearch
        });
    
    }, [debouncedSearch]);

    const handleSearch = useCallback((value: string) => {
        setSearchText(value);
    }, [searchText]);
  

    const onFinish = (values: CreateTemplate) => {
        setFormData(values);
    };

  return (
    <div className={styles.tableHeader}>
      <Form form={form} layout="vertical" onFinish={onFinish}>
        <Form.Item
          label="Digest"
          name="digestId"
          key="digestId"
          rules={[{ required: true, message: 'Please select a digest' }]}
        >
    
        <Select
            showSearch
            placeholder="Search for a digest"
            optionFilterProp="children"
            loading={loading}
            onSearch={handleSearch}
            filterOption={false}
            notFoundContent={loading ? <Spin size="small" /> : null}
        >
            {digests?.map((digest) => (
                <Option key={digest.digestId} value={digest.digestId}>
                    {digest.name} (schedule Type: {digest.scheduleType})
                </Option>
            ))}
        </Select>
        </Form.Item>

        <Form.Item
          label="Template Name"
          name="name"
          rules={[{ required: true, message: 'Please input the template name' }]}
        >
          <Input placeholder="Enter template name" />
        </Form.Item>

        <Form.Item
          label="Description"
          name="description"
          rules={[{ required: false, message: 'Please enter a description' }]}
        >
          <Input.TextArea placeholder="Enter description" />
        </Form.Item>

        <Form.Item
          label="Category"
          name="category"
          rules={[{ required: true, message: 'Please select a category' }]}
        >
          <Select placeholder="Select a category">
            {
                digestTemplateCategories?.map((category) => (
                    <Option key={category} value={category}>
                      {category}
                    </Option>
                  ))
            }
          </Select>
        </Form.Item>

        <Form.Item
            label="Icon"
            name="icon"
            rules={[{ required: false, message: 'Please select an icon' }]}
        >
            <Select
                showSearch
                placeholder="Select an icon"
                style={{ width: '100%' }}
                optionFilterProp="children"
                filterOption={(input, option) =>
                    (option?.value as string)?.toLowerCase().includes(input.toLowerCase())
                }
            >
                {getIconList().map((iconName) => (
                    <Select.Option key={iconName} value={iconName}>
                        {renderIcon(iconName)}
                    </Select.Option>
                ))}
            </Select>
        </Form.Item>
      </Form>
    </div>
  );
})

export default DigestTemplateForm;

